/**
 * Google Analytics 4 / Google Tag Manager Helper Functions
 * 
 * This module provides utility functions for tracking events via Google Tag Manager's dataLayer.
 * All functions are safe to call in any environment (dev/prod) and will only track in production.
 * 
 * Usage:
 * ```typescript
 * import { trackEvent, trackPageView, trackPurchase } from '@/lib/analytics';
 * 
 * // Track custom event
 * trackEvent('button_click', { button_name: 'hero_cta' });
 * 
 * // Track page view (handled automatically by usePageTracking hook)
 * trackPageView('/services');
 * 
 * // Track purchase
 * trackPurchase({
 *   transaction_id: 'ABC123',
 *   value: 150,
 *   currency: 'TRY',
 *   items: [...]
 * });
 * ```
 */

type DataLayerEvent = Record<string, any>;

/**
 * Push event to GTM dataLayer
 * Safe to call in dev (will only log to console)
 */
function pushToDataLayer(event: DataLayerEvent) {
  const isProduction = import.meta.env.PROD;
  const hasGTM = import.meta.env.VITE_GTM_ID;

  if (!hasGTM) {
    // GTM not configured
    return;
  }

  // Defensively initialize dataLayer to prevent race conditions
  // This runs in both dev and production for consistency
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    
    // In production, push to GTM
    if (isProduction) {
      window.dataLayer.push(event);
    }
  }

  // Log in development for debugging
  if (!isProduction) {
    console.log('[Analytics]', event);
  }
}

/**
 * Track generic custom event
 */
export function trackEvent(eventName: string, params?: Record<string, any>) {
  pushToDataLayer({
    event: eventName,
    ...params,
  });
}

/**
 * Track page view
 * This is called automatically by the usePageTracking hook on route changes
 */
export function trackPageView(path: string, title?: string) {
  pushToDataLayer({
    event: 'page_view',
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

/**
 * Track user sign up
 */
export function trackSignUp(method: 'google' | 'email' = 'google', customerType?: 'individual' | 'corporate') {
  pushToDataLayer({
    event: 'sign_up',
    method,
    customer_type: customerType,
  });
}

/**
 * Track user login
 */
export function trackLogin(method: 'google' | 'email' = 'google') {
  pushToDataLayer({
    event: 'login',
    method,
  });
}

/**
 * Track booking/purchase completion
 * For appointments/reservations
 */
export function trackPurchase(params: {
  transaction_id: string;
  value: number;
  currency?: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    item_category?: string;
    price: number;
    quantity?: number;
  }>;
  customer_type?: 'guest' | 'registered';
  payment_method?: string;
}) {
  pushToDataLayer({
    event: 'purchase',
    transaction_id: params.transaction_id,
    value: params.value,
    currency: params.currency || 'TRY',
    items: params.items || [],
    customer_type: params.customer_type,
    payment_method: params.payment_method,
  });
}

/**
 * Track when user begins checkout/booking flow
 */
export function trackBeginCheckout(value?: number, items?: any[]) {
  pushToDataLayer({
    event: 'begin_checkout',
    currency: 'TRY',
    value: value || 0,
    items: items || [],
    checkout_type: 'booking',
  });
}

/**
 * Track service/product view
 */
export function trackViewItem(params: {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
}) {
  pushToDataLayer({
    event: 'view_item',
    currency: 'TRY',
    value: params.price || 0,
    items: [
      {
        item_id: params.item_id,
        item_name: params.item_name,
        item_category: params.item_category,
        price: params.price || 0,
      },
    ],
  });
}

/**
 * Track service/product list view
 */
export function trackViewItemList(listId: string, listName: string, items?: any[]) {
  pushToDataLayer({
    event: 'view_item_list',
    item_list_id: listId,
    item_list_name: listName,
    items: items || [],
  });
}

/**
 * Track service/product selection (click)
 */
export function trackSelectItem(params: {
  item_id: string;
  item_name: string;
  item_category?: string;
  list_id?: string;
  list_name?: string;
}) {
  pushToDataLayer({
    event: 'select_item',
    item_list_id: params.list_id,
    item_list_name: params.list_name,
    items: [
      {
        item_id: params.item_id,
        item_name: params.item_name,
        item_category: params.item_category,
      },
    ],
  });
}

/**
 * Track adding service to cart/booking
 */
export function trackAddToCart(params: {
  item_id: string;
  item_name: string;
  item_category?: string;
  price: number;
  quantity?: number;
}) {
  pushToDataLayer({
    event: 'add_to_cart',
    currency: 'TRY',
    value: params.price * (params.quantity || 1),
    items: [
      {
        item_id: params.item_id,
        item_name: params.item_name,
        item_category: params.item_category,
        price: params.price,
        quantity: params.quantity || 1,
      },
    ],
  });
}

/**
 * Track site search
 */
export function trackSearch(searchTerm: string, searchType?: string) {
  pushToDataLayer({
    event: 'search',
    search_term: searchTerm,
    search_type: searchType || 'general',
  });
}

/**
 * Track form submission
 */
export function trackFormSubmit(formName: string, formType?: string) {
  pushToDataLayer({
    event: 'form_submit',
    form_name: formName,
    form_type: formType,
  });
}

/**
 * Track lead generation (corporate quote, etc.)
 */
export function trackGenerateLead(params: {
  form_type: string;
  value?: number;
  currency?: string;
}) {
  pushToDataLayer({
    event: 'generate_lead',
    form_type: params.form_type,
    currency: params.currency || 'TRY',
    value: params.value || 0,
  });
}

/**
 * Track promotion/banner view
 */
export function trackViewPromotion(params: {
  promotion_id: string;
  promotion_name: string;
  creative_name?: string;
}) {
  pushToDataLayer({
    event: 'view_promotion',
    promotion_id: params.promotion_id,
    promotion_name: params.promotion_name,
    creative_name: params.creative_name,
  });
}

/**
 * Track CTA button click
 */
export function trackCTAClick(buttonName: string, section?: string) {
  pushToDataLayer({
    event: 'cta_click',
    button_name: buttonName,
    section: section,
  });
}

/**
 * Track content selection (accordion, tabs, etc.)
 */
export function trackSelectContent(contentType: string, itemId: string) {
  pushToDataLayer({
    event: 'select_content',
    content_type: contentType,
    item_id: itemId,
  });
}

/**
 * Track user contact attempt
 */
export function trackContactClick(method: 'phone' | 'email' | 'whatsapp', contactInfo?: string) {
  pushToDataLayer({
    event: 'contact_click',
    method,
    contact_info: contactInfo,
  });
}

/**
 * Track booking step completion (for multi-step forms)
 */
export function trackBookingStep(stepNumber: number, stepName: string, progressPercentage?: number) {
  pushToDataLayer({
    event: 'booking_step_completed',
    step_number: stepNumber,
    step_name: stepName,
    progress_percentage: progressPercentage,
  });
}

/**
 * Track booking abandonment
 */
export function trackBookingAbandoned(params: {
  step_name: string;
  vehicle_type?: string;
  service_selected?: boolean;
}) {
  pushToDataLayer({
    event: 'booking_abandoned',
    step_name: params.step_name,
    vehicle_type: params.vehicle_type,
    service_selected: params.service_selected,
  });
}

/**
 * Set user properties (called once after login)
 */
export function setUserProperties(properties: {
  user_type?: 'customer' | 'employee' | 'cashier' | 'admin';
  customer_type?: 'individual' | 'corporate';
  loyalty_tier?: 'bronze' | 'silver' | 'gold';
  loyalty_points?: number;
  subscription_status?: 'active' | 'paused' | 'expired' | 'none';
}) {
  pushToDataLayer({
    event: 'user_properties_set',
    user_properties: properties,
  });
}

/**
 * Track error/exception
 */
export function trackException(description: string, fatal: boolean = false) {
  pushToDataLayer({
    event: 'exception',
    description,
    fatal,
  });
}

declare global {
  interface Window {
    dataLayer: any[];
  }
}
